import { combineLatest, EMPTY, Observable, Subscription } from 'rxjs';

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { IAvailableFund } from '@models/available-fund.class';
import { Totals } from '@models/contract.class';
import { IFunds } from '@models/fund.class';
import { OperationError } from '@models/operationError.class';
import { GENERAL_CODE_CALLBACKS, TradeInfo } from '@models/trade-info.class';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs/operators';
import {
  AppState,
  FUNDS_ALLOCATION,
  FUNDS_OPERATION_UNITS,
  NO_SCROLL,
  OPERATION_MODAL_OPENED,
  OPERATION_RESET,
  TRACKING_EVENTS,
  USER_ROLES,
} from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import * as FundsActions from 'src/app/state/actions/funds.actions';
import {
  NEW_RELIC_KNOWN_ERRORS,
  NewRelicLogger,
} from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import {
  ContractsService,
  NotificationService,
  OperationService,
} from 'src/core';
import {
  AnalyticsTrackingService,
  FundType,
  TrackingIssuerType,
} from 'src/core/services/analyticsTracking.service';

@Component({
  selector: 'hb-trade-funds',
  templateUrl: './tradeFunds.component.html',
})
export class TradeFundsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() operationStatus: boolean;
  @Input() isGeolocationEnabled: boolean;
  @Input() isPositionMocked: boolean;
  @Input() shouldStartLoaderOnOperationAttempt: boolean;
  @Input() shouldNotifyGeolocationOnOperationAttempt: boolean;

  @Output() changeOperationStatus = new EventEmitter<boolean>();

  public FUNDS_TYPE: any = {
    27: 'Deuda',
    28: 'Renta Variable',
  };

  public fondosList: IAvailableFund[] = [];
  private fondosDeuda: IAvailableFund[];
  private fondosRenta: IAvailableFund[];
  public externalFundList: any[];
  public readonly FUNDS_ALLOCATION = FUNDS_ALLOCATION;

  selectedTab: number;
  selectedFund: any;

  FONDOS_DEUDA = 27;
  FONDOS_RENTA = 28;

  selectedTabTitle = 'Fondos de renta variable';
  searchField: string;

  position: any = {};
  currentPosition: any = [];

  public isInputInvalid: boolean;

  public operationDate: string;
  public openTradeTime: string;
  public closeTradeTime: string;
  public buyOperationDate: string;
  public sellOperationDate: string;
  public settlementBuyDate: string;
  public settlementSellDate: string;
  public externalFundCloseOperationHour: string;

  public userHasIssue = false;
  public showBuyMessage = false;
  public showSellMessage = false;
  public emptyState = true;
  public showOutOfService = false;
  public showResetButton = false;
  public showSearchResults = false;

  public saleType: string;
  public copyCallToAction: string;
  public titles = 0;
  public duration = 1;
  public totalToSpend: any = 0;
  public fundPrice: number;
  public showConfirmation: boolean;
  public lockConfirmation = true;
  public canOperate = true;
  public lockCancel = false;
  public isDiscretional: boolean;
  public isLinked = false;
  public plusMinus = false;
  public attachedClick = true;
  public showMessageMMA = false;
  public showMessageRI = false;

  public isBuyButtonLoading: boolean;
  public isSellButtonLoading: boolean;

  public fundsOperationUnits = FUNDS_OPERATION_UNITS;
  public operationUnit: FUNDS_OPERATION_UNITS = FUNDS_OPERATION_UNITS.amount;
  public operationUnitsSwitcher: { [key: string]: FUNDS_OPERATION_UNITS } = {
    [FUNDS_OPERATION_UNITS.amount]: FUNDS_OPERATION_UNITS.titles,
    [FUNDS_OPERATION_UNITS.titles]: FUNDS_OPERATION_UNITS.amount,
  };

  private currentContractSubscription: Subscription;
  private positionSymbolSubscription: Subscription;
  private userLoggedInSubscription: Subscription;
  private operationTimeSubscription: Subscription;

  private fondosURL: IFunds;
  private currentContract: any;
  private fiscalType: number;

  private affiliateType: number;

  private isOperable = false;

  private order: any = {};

  private userGlobalSubscription: Subscription;
  private userRoleSubscription: Subscription;
  private isUserGlobal = false;
  private userRole: any;

  private fundsLoaded = false;

  private selectedFundSubscription: Subscription;
  private elPatronDataSubscription: Subscription;
  private elPatronUrl: any;
  private readToken: any;
  private isSellAvailable: boolean;
  private isBuyAvailable: boolean;
  private totalPosition: Totals;

  private tradeInfo: TradeInfo;
  private prospectlistFunds = [
    'GBMLATM BO',
    'GBMPBOL B',
    'GBMPCON B',
    'REGIOB B',
    'REGIOC B',
  ];
  constructor(
    private el: ElementRef,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private operationService: OperationService,
    private analyticsTrackingService: AnalyticsTrackingService,
    private newRelicLogger: NewRelicLogger,
    private contractsService: ContractsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    const { operationStatus } = changes;
    if (operationStatus && operationStatus.currentValue !== undefined) {
      this.emptyState = !operationStatus.currentValue;
    }
  }

  ngOnInit() {
    const RESET_FUNDS = false;

    this.selectedTab = this.FONDOS_RENTA;

    this.userLoggedInSubscription = this.store
      .select('loggedInUser')
      .pipe(
        map((value: any) => value.toJS()),
        map((value: any) => {
          return {
            readToken: value.sessionToken,
          };
        }),
      )
      .subscribe((userInfo: any) => {
        this.readToken = userInfo.readToken;
      });

    this.currentContractSubscription = this.store
      .select('currentContract')
      .pipe(
        map((values: any) => values.toJS()),
        distinctUntilChanged(),
        filter((value: any) => {
          return Object.keys(value).length > 0;
        }),
      )
      .subscribe((value: any) => {
        this.getDiscretionalStatus(value.isDiscretional);

        if (
          this.currentContract === undefined ||
          this.currentContract.contractID !== value.contractID
        ) {
          this.fiscalType = value.legalPersonalityType;
          this.affiliateType = value.affiliateTypeId;
          this.currentContract = value;

          const gbmFundSubscription: Observable<any> =
            this.operationService.GetAllAvailableFunds(this.fiscalType);
          const externalFundSubscription: Observable<any> =
            this.operationService.getExternalFunds(this.fiscalType);

          if (featureFlags.tmpExternalFundsOperable.isEnabled()) {
            combineLatest(
              gbmFundSubscription,
              externalFundSubscription,
            ).subscribe(
              ([gbmFunds, externaFunds]) => {
                this.fondosList = gbmFunds;
                this.fondosList = this.fondosList.concat(externaFunds);
                this.fundsLoaded = true;
              },
              () => {
                // Just catch error
              },
            );
          } else {
            gbmFundSubscription.subscribe(
              (gbmFunds: any) => {
                this.fondosList = gbmFunds;
                this.fundsLoaded = true;
              },
              () => {
                // Just catch error
              },
            );
          }
        }
        this.reset(false, RESET_FUNDS);
      });

    this.positionSymbolSubscription = this.store
      .select('position')
      .pipe(
        map((values: any) => values.toJS()),
        distinctUntilChanged(),
      )
      .subscribe((positions: any[]) => {
        this.currentPosition = [];
        if (positions) {
          Object.getOwnPropertyNames(positions).forEach((item: any) => {
            if (
              positions[item][0] !== undefined &&
              (positions[item][0].positionValueType === 2 ||
                positions[item][0].positionValueType === 5 ||
                positions[item][0].positionValueType === 10)
            ) {
              positions[item].forEach((fondo: any) => {
                this.currentPosition.push(fondo);
              });
            }
          });
        }
      });

    this.store
      .select('currentContract')
      .pipe(
        map((contractTotals: any) => contractTotals.get('totals')),
        filter((totals: any) => !!totals),
        map((totals) => totals.toJS()),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      )
      .subscribe((totalsValue: Totals) => {
        this.totalPosition = totalsValue;
      });

    this.store.dispatch(new FundsActions.RequestFunds());
    this.operationService.getFundsURL().subscribe((value: IFunds) => {
      this.store.dispatch(new FundsActions.FundsReceived(value));
      this.fondosURL = value;
    });

    this.selectedFundSubscription = this.store
      .select('currentFund')
      .pipe(
        filter((value: any) => value !== undefined),
        map((value: any) => value.toJS()),
      )
      .subscribe((value: any) => {
        this.loadSelectedFund(value);
      });

    this.scanForUserRole();
    this.scanForUserTypeContract();
    this.scanForElPatronData();
  }

  ngOnDestroy() {
    this.setSearchResults(false);
    [
      this.currentContractSubscription,
      this.userLoggedInSubscription,
      this.positionSymbolSubscription,
      this.userGlobalSubscription,
      this.selectedFundSubscription,
      this.operationTimeSubscription,
    ]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
  }

  public get missingInformationText(): string {
    return this.isPositionMocked && this.selectedFund ? 'No disponible' : '--';
  }

  public get shouldDisplayIssueData(): boolean {
    return this.userHasIssue && !this.isPositionMocked;
  }

  closeWhenOutsideClick(event: any) {
    if (this.showSearchResults) {
      this.attachedClick = !this.attachedClick;
      this.setSearchResults(!this.showSearchResults);
    }
  }

  getURL(issueID: string, data: any) {
    const newData = (data || []).filter((item: any, index: number) => {
      return item.name === issueID;
    });

    if (newData.length > 0) {
      return newData[0].url;
    }
  }

  getFactSheetURL(symbol: string) {
    const timestamp = new Date().getTime();
    return `https://assets.gbm.com/trading/fact-sheets/${symbol}.pdf?v=${timestamp}`;
  }

  isHere(issueID: string, data: any) {
    const newData = data.filter((item: any, index: number) => {
      return item.name === issueID;
    });
    if (newData.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  isInProspectList(issueID: string) {
    return this.prospectlistFunds.indexOf(issueID) >= 0;
  }

  searchFondo(issueID: string) {
    if (!this.fondosURL || !this.fondosURL.business) {
      return false;
    }

    const self = this;
    const bizDebt = this.fondosURL.business.debt.funds;
    const bizEquity = this.fondosURL.business.equity.funds;
    const indiDebt = this.fondosURL.individuals.debt.funds;
    const indiEquity = this.fondosURL.individuals.equity.funds;
    const indiOffFunds = this.fondosURL.individuals.fundOfFunds.funds;
    const data = [bizDebt, bizEquity, indiDebt, indiEquity, indiOffFunds];

    const aux = data.filter((item: any) => {
      if (self.isHere(issueID, item)) {
        return true;
      }
    });

    return this.getURL(issueID, aux[0]);
  }

  setSearchResults(block: boolean = true) {
    this.showSearchResults = block;

    this.store.dispatch({
      type: NO_SCROLL,
      payload: { block, source: 'TradeFundsComponent' },
    });
  }

  setHardReset() {
    this.operationTimeSubscription &&
      this.operationTimeSubscription.unsubscribe();
    this.showResetButton = false;
    this.reset(false, false);
  }

  reset(broadcast: boolean = true, resetFunds: boolean = true) {
    const parent = this.el.nativeElement;
    this.userHasIssue = false;
    this.titles = 0;
    this.totalToSpend = 0;
    this.fundPrice = 0;
    this.isLinked = false;
    this.showOutOfService = false;
    this.showMessageRI = false;
    this.searchField = '';
    this.position = {};
    this.showConfirmation = false;
    this.lockConfirmation = true;
    this.showResetButton = false;
    this.lockCancel = false;

    this.selectedFund = undefined;

    if (resetFunds) {
      this.fundsLoaded = false;
    }

    if (parent) {
      const btnCompra = parent.querySelector('.btn-compra');
      const btnVenta = parent.querySelector('.btn-venta');

      if (btnCompra) {
        btnCompra.classList.remove('active');
      }
      if (btnVenta) {
        btnVenta.classList.remove('active');
      }
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });

    if (broadcast) {
      this.store.dispatch({
        type: OPERATION_RESET,
        payload: { reset: true },
      });
    }
  }

  changeType(type: number, resetFunds: boolean = true) {
    this.showResetButton = false;

    this.reset(false, resetFunds);
  }

  getFundsType(fondos: any, INSTRUMENT: number) {
    let index = 0;
    return fondos.filter((item: any) => {
      if (
        item.instrumentType === INSTRUMENT &&
        item.legalPersonalityType === this.fiscalType
      ) {
        item.name = item.issueID;
        item.id = index++;
        return true;
      }
    });
  }

  getFundsforAffiliate(fondos: any, INSTRUMENT: number) {
    let index = 0;
    return fondos.filter((item: any) => {
      if (
        item.instrumentType === INSTRUMENT &&
        item.legalPersonalityType === this.fiscalType &&
        item.symbol !== 'MMACONS'
      ) {
        item.name = item.issueID;
        item.id = index++;
        return true;
      }
    });
  }

  scanForElPatronData() {
    this.elPatronDataSubscription = this.store
      .select('elPatronData')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => value.URL_SETTINGS !== undefined),
        take(1),
      )
      .subscribe((value: any) => {
        this.elPatronUrl = value;
      });
  }

  getDiscretionalStatus(discretionalParameter: any) {
    this.isDiscretional = discretionalParameter;
  }

  loadSelectedFund(fund: any) {
    const WAIT = 200;
    const RESET_FUND = false;
    const fundType: number = fund.valueType === 2 ? 28 : 27;
    const selectorType: string =
      fund.valueType === 2 ? 'renta-variable' : 'deuda';

    if (!this.fundsLoaded) {
      setTimeout(() => {
        this.loadSelectedFund(fund);
      }, WAIT);
    } else {
      this.changeType(fundType, RESET_FUND);
      this.setOrderType(fund.issueID);

      this.store.dispatch(new FundsActions.SelectFund());
    }
  }

  parseTotalToSpend(total: string): number {
    if (typeof total === 'string') {
      let spend = total.replace(/\,/g, '');
      spend = spend.trim().replace(/\$/g, '').replace(/MXN/g, '');
      return parseFloat(spend);
    }
    return total;
  }

  adjustRatio(change: string = 'titles') {
    let floorTitles: any;

    if (change === 'titles') {
      this.totalToSpend = this.fundPrice * this.titles;
    } else {
      this.totalToSpend = this.parseTotalToSpend(this.totalToSpend);
      floorTitles = parseInt('' + this.totalToSpend / this.fundPrice, 10);
      this.titles = !isNaN(floorTitles) ? floorTitles : 0;
      this.totalToSpend = this.fundPrice * this.titles;
    }
    this.isLinked = false;
  }

  setTotal($event: any) {
    if (
      (typeof $event === 'string' && $event !== '') ||
      typeof $event === 'number'
    ) {
      this.plusMinus = false;
      this.isLinked = true;
      this.totalToSpend = Number($event);
      this.adjustRatio('spend');
      this.isValidPrice(true);
      this.isOrderReady(true);
    }
  }

  setTitles($event: any) {
    if (
      (typeof $event === 'string' && $event !== '') ||
      typeof $event === 'number'
    ) {
      this.plusMinus = true;
      this.isLinked = true;
      this.titles = Number($event);
      this.adjustRatio('titles');
      this.isValidTitles(true);
      this.isOrderReady(true);
    }
  }

  /**
   * Update the number of titles for a certain total
   *
   * This does a "soft" update over the titles, as it doesn't update
   * the total to operate, which would interrupt the flow of setting the
   * amount using the amount input field
   * @param total to spend in the operation
   */
  public obtainTitlesEquivalent(total?: number) {
    if (total === undefined || total === null || !this.fundPrice) {
      return;
    }
    const floorTitles = parseInt('' + total / this.fundPrice, 10);
    this.titles = !isNaN(floorTitles) ? floorTitles : 0;
  }

  getSelectedFund(fund: any) {
    let auxFund: any;
    if (this.selectedTab === this.FONDOS_DEUDA) {
      auxFund = this.fondosDeuda.filter((item: any) => {
        if (item.issueID === fund) {
          return true;
        }
      });
    } else if (this.selectedTab === this.FONDOS_RENTA) {
      auxFund = this.fondosRenta.filter((item: any) => {
        if (item.issueID === fund) {
          return true;
        }
      });
    }
    return auxFund[0];
  }

  parseMonth(month: number) {
    switch (month) {
      case 0:
        return 'ENE';
      case 1:
        return 'FEV';
      case 2:
        return 'MAR';
      case 3:
        return 'ABR';
      case 4:
        return 'MAY';
      case 5:
        return 'JUN';
      case 6:
        return 'JUL';
      case 7:
        return 'AGO';
      case 8:
        return 'SEPT';
      case 9:
        return 'OCT';
      case 10:
        return 'NOV';
      case 11:
        return 'DIC';
      default:
        return '--';
    }
  }

  getLiqDate(plusDates: number) {
    const today: any = new Date();

    if (isNaN(plusDates)) {
      plusDates = 0;
    }
    today.setDate(today.getDate() + plusDates);

    if (today.getDay() === 5) {
      today.setDate(today.getDate() + 2);
    } else if (today.getDay() === 6) {
      today.setDate(today.getDate() + 1);
    }

    return today.getDate() + '/' + this.parseMonth(today.getMonth());
  }

  buildPosition(data: any) {
    let item: any;
    this.fundPrice = this.selectedFund.lastPrice;
    this.isOperable = this.selectedFund.isOperableToday;
    this.showOutOfService = !this.selectedFund.isOperableToday;
    this.isSellAvailable = this.selectedFund.isSellAvailable;
    this.isBuyAvailable = this.selectedFund.isBuyAvailable;
    const optionsHours = {
      hour: 'numeric' as 'numeric',
      minute: 'numeric' as 'numeric',
      hour12: false,
    };

    const operationDateTemp = new Date(this.selectedFund.operationDate);
    const openTradeTimeTemp = new Date(this.selectedFund.openTradeTime);
    const closeTradeTimeTemp =
      this.selectedFund.issueFundAllocation === FUNDS_ALLOCATION.external
        ? new Date(this.selectedFund.closeOperationTimeEnd)
        : new Date(this.selectedFund.tradeTime);

    const buyOperationDateTemp = new Date(this.selectedFund.buyOperationDate);
    const sellOperationDateTemp = new Date(this.selectedFund.sellOperationDate);
    this.buyOperationDate = moment(buyOperationDateTemp).format('LL');
    this.sellOperationDate = moment(sellOperationDateTemp).format('LL');

    const settlementBuyDateTemp = new Date(this.selectedFund.settlementBuyDate);
    const settlementSellDateTemp = new Date(
      this.selectedFund.settlementSellDate,
    );
    this.settlementBuyDate = moment(settlementBuyDateTemp).format('LL');
    this.settlementSellDate = moment(settlementSellDateTemp).format('LL');

    this.operationDate = moment(operationDateTemp).format('LL');
    this.openTradeTime = openTradeTimeTemp
      .toLocaleString('en-US', optionsHours)
      .toLowerCase();
    this.closeTradeTime = closeTradeTimeTemp
      .toLocaleString('en-US', optionsHours)
      .toLowerCase();

    if (this.buyOperationDate === this.sellOperationDate) {
      this.showSellMessage = false;
      this.showBuyMessage = false;
    } else {
      this.showSellMessage = true;
      this.showBuyMessage = true;
    }

    if (data.length > 0) {
      item = data[0];
      this.userHasIssue = true;
      this.position.liqDate = this.getLiqDate(item.mfLiqTypeBuy);
      this.position.shares = item.quantity;
      this.position.marketValue = item.marketValue;

      // Plus Minus calculation
      this.position.plusMinus = item.yieldValue;

      // % avg hist
      this.position.var = item.historicalVariationPercentage;
    } else {
      this.userHasIssue = false;
    }

    if (this.selectedFund.issueFundAllocation === FUNDS_ALLOCATION.gbm) {
      this.selectedFund.fundURL = this.isInProspectList(
        this.selectedFund.issueID,
      )
        ? this.searchFondo(this.selectedFund.issueID)
        : this.getFactSheetURL(this.selectedFund.symbol);
    }
  }

  userHasPosition() {
    const aux: any = this.currentPosition.filter((item: any) => {
      if (item.issueId === this.selectedFund.issueID) {
        item.isOperable = this.selectedFund.isOperableToday;
        return true;
      }
    });

    this.buildPosition(aux);
  }

  setOrderType(name: any) {
    this.setHardReset();
    this.emptyState = true;
    this.changeOperationStatus.emit(this.emptyState);
    this.selectedFund = this.getFund(name)[0];
    if (this.selectedFund !== undefined) {
      this.selectedFund.issueFundAllocation === FUNDS_ALLOCATION.external &&
        this.selectedFund.isOperableToday &&
        this.validateOperationTime();

      this.searchField = this.selectedFund.issueID;
      this.userHasPosition();
      this.setSearchResults(false);

      this.showResetButton = true;
    } else {
      this.showOutOfService = true;
      this.searchField = name;
      this.showResetButton = true;
    }
  }

  isValidPrice(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.totalToSpend <= 0 || isNaN(this.totalToSpend)) {
      if (!onlyCheck) {
        this.isInputInvalid = true;
      }
      return false;
    } else {
      if (!onlyCheck) {
        this.isInputInvalid = false;
      }
      return true;
    }
  }

  isValidTitles(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.titles <= 0 || isNaN(this.titles)) {
      if (!onlyCheck) {
        parent.querySelector('.order-titles').classList.add('invalid');
      }
      return false;
    } else {
      if (!onlyCheck) {
        parent.querySelector('.order-titles').classList.remove('invalid');
      }
      return true;
    }
  }

  isIssueValid() {
    return this.selectedFund.issueID !== undefined && this.isOperable;
  }

  isOrderReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    const btnCompra = parent.querySelector('.btn-compra');
    const btnVenta = parent.querySelector('.btn-venta');

    if (!this.canOperate || this.isDiscretional || this.showMessageRI) {
      return;
    }

    if (!this.isValidPrice(onlyCheck) || !this.isIssueValid()) {
      if (btnCompra) {
        btnCompra.classList.remove('active');
      }
      if (btnVenta) {
        btnVenta.classList.remove('active');
      }
      return false;
    } else {
      if (this.isBuyAvailable) {
        if (btnCompra) {
          btnCompra.classList.add('active');
        }
      }
      if (this.userHasIssue && this.isSellAvailable) {
        if (btnVenta) {
          btnVenta.classList.add('active');
        }
      }
      return true;
    }
  }

  buildOrder(orderType: string, maxPosition?: boolean) {
    const MINIMUM_PRICE = 100000;

    const isBuy: boolean = orderType === 'compra';
    const totalFundPrice = this.totalToSpend.toFixed(6);

    if (this.selectedFund.issueID.toString().trim().includes('MMACONS')) {
      if (totalFundPrice < MINIMUM_PRICE) {
        this.showMessageMMA = true;
        this.lockConfirmation = true;
      } else {
        this.showMessageMMA = false;
        this.lockConfirmation = false;
      }
    } else {
      this.showMessageMMA = false;
      this.lockConfirmation = true;
    }

    this.lockConfirmation = false;

    this.order = {
      accountId: this.currentContract.contractID,
      amount: !isBuy && maxPosition ? 0 : totalFundPrice,
      bitBuy: isBuy,
      instrumentType: this.selectedFund.instrumentType,
      issueId: this.selectedFund.issueID,
      quantity: !isBuy && maxPosition ? this.titles : undefined,
      isTitles: !isBuy && maxPosition,
    };
  }

  sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  tryBuy() {
    if (!this.isBuyAvailable) {
      return false;
    }

    const totalFundAmount = this.totalToSpend.toFixed(6);
    if (!this.isValidPosition(totalFundAmount, true)) {
      return;
    }

    if (this.isOrderReady()) {
      this.buildOrder('compra');
      this.saleType = 'compra';
      this.copyCallToAction = 'Comprar';

      this.setShowConfirmation(true);
    } else {
      this.setShowConfirmation();
    }
  }

  trySell() {
    if (!this.userHasIssue || !this.isSellAvailable) {
      return false;
    }

    const totalFundAmount = this.totalToSpend.toFixed(6);
    const maxPosition = this.titles === this.position.shares;

    if (!this.isValidPosition(totalFundAmount, false, maxPosition)) {
      return;
    }

    if (this.isOrderReady()) {
      this.buildOrder('venta', maxPosition);
      this.saleType = 'venta';
      this.copyCallToAction = 'Vender';

      this.setShowConfirmation(true);
    } else {
      this.setShowConfirmation();
    }
  }

  setShowConfirmation(toShow: boolean = false) {
    this.showConfirmation = toShow;

    this.tradeInfo = new TradeInfo(
      false,
      this.order,
      TrackingIssuerType.MutualFunds,
      window.location.pathname,
      this.titles.toString(),
      this.saleType,
      this.totalToSpend.toFixed(6).toString(),
      this.fundPrice.toString(),
      undefined,
      this.selectedFund.issueFundAllocation === FUNDS_ALLOCATION.gbm
        ? FundType.gbm
        : this.selectedFund.issueName,
    );

    const tradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

    if (this.showConfirmation) {
      this.analyticsTrackingService.logEvent(
        TRACKING_EVENTS.start_trade.event,
        tradeInfoData,
      );
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelConfirmation() {
    this.showConfirmation = false;
    this.lockConfirmation = true;
    this.lockCancel = false;

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  registerOrder() {
    const startTime: any = new Date();
    const hash: any = this.newRelicLogger.buildHash(
      startTime.getTime(),
      this.currentContract.contractID,
      this.selectedFund.issueID,
      this.titles,
      'FON',
    );
    this.order.hash = hash;

    this.operationService
      .registerFundsOrder(this.order)
      .pipe(
        catchError((error: OperationError) => {
          const customAttributes = {
            _type: 'Funds',
          };

          this.newRelicLogger.setEvent(
            NEW_RELIC_KNOWN_ERRORS.tradeError,
            error,
            customAttributes,
          );

          this.reset();
          this.fundsLoaded = true;

          this.tradeInfo.setSuccess(false);
          this.tradeInfo.setCallback(error.message);

          const tradeErrorInfoData = this.tradeInfo.getAnaliticsTradeInfo();

          this.analyticsTrackingService.logEvent(
            TRACKING_EVENTS.trade.event,
            tradeErrorInfoData,
          );
          return EMPTY;
        }),
      )
      .subscribe((value: any) => {
        this.reset();
        this.fundsLoaded = true;

        this.notificationService.showSuccess({
          title: 'Orden Ejecutada',
          message: `Orden ${value.response} ejecutada con éxito.`,
          eventId: undefined,
          isBusinessError: false,
        });

        this.tradeInfo.setSuccess(true);
        this.tradeInfo.setCallback(GENERAL_CODE_CALLBACKS.SUCCESS);
        const tradeSuccessInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          tradeSuccessInfoData,
        );
        const isRealTime = true;
        this.contractsService.getPositionSummaryBatch(
          this.currentContract.contractID,
          isRealTime,
        );
      });
  }

  doMovement() {
    if (this.lockConfirmation) {
      return;
    }

    this.lockCancel = true;
    this.lockConfirmation = true;

    this.registerOrder();
  }

  public switchOperationUnits() {
    this.operationUnit = this.operationUnitsSwitcher[this.operationUnit];
    this.cdr.detectChanges();
  }

  public sellEverything() {
    this.operationUnit =
      this.operationUnitsSwitcher[FUNDS_OPERATION_UNITS.amount];

    this.setTitles(this.position.shares);
  }

  public navDown(event: any) {
    const code = event.keyCode;
    const source = event.srcElement;

    let issueid: string;

    if (code === 13) {
      if (source.classList.contains('option')) {
        issueid = event.currentTarget.dataset.issueid;

        this.setOrderType(issueid);
      }
    } else if (code === 40) {
      if (source.classList.contains('search')) {
        // input
        const optionEl =
          source.parentElement.parentElement.querySelector('.option');
        if (optionEl) {
          optionEl.focus();
        }
      } else if (source.classList.contains('option')) {
        // option
        if (source.nextElementSibling === null) {
          return;
        }
        source.nextElementSibling.focus();
      }
    } else if (code === 38) {
      // up
      if (source.classList.contains('option')) {
        if (source.previousElementSibling === null) {
          return;
        }
        source.previousElementSibling.focus();
      }
    }
  }

  private scanForUserTypeContract() {
    this.userGlobalSubscription = this.store
      .select('userGlobal')
      .subscribe((value: any) => {
        this.isUserGlobal = value;
      });
  }

  private scanForUserRole() {
    this.userRoleSubscription = this.store
      .select('userRole')
      .pipe(filter((value: any) => value !== undefined))
      .subscribe((value: any) => {
        this.userRole = value;
        this.canOperate =
          value === USER_ROLES.RI ||
          value === USER_ROLES.SUBDIRECTOR ||
          value === USER_ROLES.READONLY
            ? false
            : true;
      });
  }

  private getFund(fundName: string) {
    return this.fondosList.filter((item: IAvailableFund) => {
      return item.issueID === fundName;
    });
  }

  private validateOperationTime() {
    const fundOperationTime = new Date(this.selectedFund.operationTimeEnd);
    const fundCloseOperationTime = new Date(
      this.selectedFund.closeOperationTimeEnd,
    );
    this.externalFundCloseOperationHour = moment(fundCloseOperationTime).format(
      'hh:mm',
    );

    this.operationTimeSubscription = this.store
      .select('params')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => !!value.centralHour),
        map((value) => value.centralHour),
      )
      .subscribe((centralHour: any) => {
        const currentHour = new Date(centralHour);

        const isWhithinFundHours = moment(currentHour).isSameOrBefore(
          moment(fundOperationTime),
        );

        if (currentHour === undefined || isNaN(currentHour.getTime())) {
          this.showMessageRI = true;
        } else if (isWhithinFundHours) {
          this.showMessageRI = false;
        } else {
          this.showMessageRI = true;
        }
      });
  }

  private isValidPosition(
    amount: number,
    bitBuy: boolean,
    maxPosition?: boolean,
  ): boolean {
    let message = 'No cuentas con suficiente disponible para';
    let isValid = false;
    if (bitBuy) {
      message = `${message} comprar`;
      isValid = this.totalPosition.buyingPower >= amount;
    } else {
      isValid =
        maxPosition || (!maxPosition && this.position.marketValue >= amount);
      message = `${message} vender`;
    }
    !isValid &&
      this.notificationService.showError({
        title: 'Orden',
        message,
        eventId: undefined,
        isBusinessError: false,
      });
    return isValid;
  }
}
